import { useMemo } from "react";

export default function useFormatTrancheSummaryResults(
  allTrancheTableValues,
  awardType,
  awardCalculatorValues,
) {
  const isNumerical = (value) => {
    return !isNaN(parseFloat(value));
  };

  return useMemo(() => {
    const addTrancheDetailsTogether = (
      newData,
      currentData,
      dataKey,
      currentAwardValues,
    ) => {
      const combinedData = {};
      Object.keys(newData).forEach((key) => {
        if (["ID", "trancheDate"].includes(key)) {
          combinedData[key] = newData[key];
        } else if (key === "key") {
          combinedData[key] = dataKey;
        } else if (key === "killPercentage") {
          combinedData[key] = 0;
        } else {
          const newValue =
            typeof newData?.[key] === "object"
              ? newData?.[key]?.value
              : newData?.[key];

          const adjustedKey =
            currentAwardValues?.anticipatedAward &&
            [
              "remainingUnits",
              "remainingFullValue",
              "remainingFullValueAfterTax",
            ].includes(key)
              ? `anticipated-${key}`
              : key;

          const computedValue =
            isNumerical(newValue) && isNumerical(currentData[adjustedKey])
              ? parseFloat(newValue) + parseFloat(currentData[adjustedKey])
              : isNumerical(newValue)
                ? parseFloat(newValue)
                : isNumerical(currentData[adjustedKey])
                  ? parseFloat(currentData[adjustedKey])
                  : 0;

          combinedData[adjustedKey] = computedValue;

          // Ensure the un-prefixed key always exists, but don't double-count
          // when adjustedKey === key (it'd be the same write)
          if (adjustedKey !== key && !isNumerical(combinedData[key])) {
            combinedData[key] = currentData[key];
          }
        }
      });
      return { ...combinedData };
    };

    if (
      !allTrancheTableValues[awardType] ||
      !Object.keys(allTrancheTableValues[awardType]).length
    ) {
      return [];
    }

    const allDatesAndAwards = {};
    Object.entries(allTrancheTableValues[awardType]).forEach(
      ([grantID, grant], index1) => {
        const currentAwardValues = awardCalculatorValues.find(
          (item) => item?.ID === grantID.replace("Grant ", "grant"),
        );

        Object.values(grant).forEach((trancheDetails, index2) => {
          const trancheDate = trancheDetails.trancheDate;
          if (!allDatesAndAwards?.[trancheDate]) {
            allDatesAndAwards[trancheDate] = addTrancheDetailsTogether(
              trancheDetails,
              {},
              `${index1}-${index2}`,
              currentAwardValues,
            );
          } else {
            allDatesAndAwards[trancheDate] = addTrancheDetailsTogether(
              trancheDetails,
              allDatesAndAwards[trancheDate],
              `${index1}-${index2}`,
              currentAwardValues,
            );
          }
        });
      },
    );
    return Object.values(allDatesAndAwards)
      .sort((a, b) => new Date(a.trancheDate) - new Date(b.trancheDate))
      .map((item, index) => ({
        ...item,
        key: index,
        ID: `Tranche ${index}`,
        killPercentage: Math.max(
          (100 * item.killAmount) / item.remainingFullValue,
          0,
        ),
      }));
  }, [allTrancheTableValues, awardType, awardCalculatorValues]);
}
